import React, { useState, useEffect, useRef } from 'react';
import { 
  getAdminProfile, 
  updateAdminProfile, 
  changeAdminPassword, 
  updateAdminSettings 
} from '../../Services/Operations/ProductAPI';
import '../../Styles/ProfileManagement.css';
import { useSelector } from 'react-redux';
import '../../Styles/ProfileManagement.css';

const SkeletonProfileImage = () => (
  <div className="profile-image-container skeleton">
    <div className="skeleton-profile-image"></div>
  </div>
);

const SkeletonStatItem = () => (
  <div className="stat-item skeleton">
    <div className="skeleton-stat-number"></div>
    <div className="skeleton-stat-label"></div>
  </div>
);

const SkeletonDetailItem = () => (
  <div className="detail-item skeleton">
    <div className="skeleton-label"></div>
    <div className="skeleton-value"></div>
  </div>
);

const SkeletonSettingItem = () => (
  <div className="setting-item skeleton">
    <div className="skeleton-label"></div>
    <div className="skeleton-value"></div>
  </div>
);

const SkeletonPreferenceItem = () => (
  <div className="preference-item skeleton">
    <div className="skeleton-checkbox"></div>
    <div className="skeleton-label"></div>
  </div>
);

const SkeletonActivityItem = () => (
  <div className="activity-item skeleton">
    <div className="activity-info">
      <div className="skeleton-activity-type"></div>
      <div className="skeleton-activity-ip"></div>
    </div>
    <div className="skeleton-activity-time"></div>
  </div>
);

const ProfileManagement = () => {
  const [profileData, setProfileData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [isEditingSettings, setIsEditingSettings] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [settingsForm, setSettingsForm] = useState({});
  const [profileImage, setProfileImage] = useState(null);
  const fileInputRef = useRef(null);
  const { token } = useSelector(state => state.user);

  const fetchProfileData = async () => {
    try {
      setLoading(true);
      const response = await getAdminProfile(token);
      
      if (response.success) {
        setProfileData(response.data);
        setEditForm({
          name: response.data.name,
          phone: response.data.phone,
          department: response.data.department
        });
        setSettingsForm(response.data.settings || {});
      }
    } catch (error) {
      console.error('Error fetching profile:', error);
      alert('Error fetching profile data: ' + (error.response?.data?.message || error.message));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProfileData();
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setEditForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSettingsChange = (e) => {
    const { name, value, type, checked } = e.target;
    setSettingsForm(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleDashboardSettingsChange = (e) => {
    const { name, value, type, checked } = e.target;
    setSettingsForm(prev => ({
      ...prev,
      dashboardPreferences: {
        ...prev.dashboardPreferences,
        [name]: type === 'checkbox' ? checked : value
      }
    }));
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (file) {

      if (!file.type.startsWith('image/')) {
        alert('Please select an image file');
        return;
      }

      if (file.size > 5 * 1024 * 1024) {
        alert('Image size should be less than 5MB');
        return;
      }

      const formData = new FormData();
      formData.append('profilePicture', file);
      if (editForm.name) formData.append('name', editForm.name);
      if (editForm.phone) formData.append('phone', editForm.phone);
      if (editForm.department) formData.append('department', editForm.department);

      try {
        const response = await updateAdminProfile(formData, token);
        
        if (response.success) {
          setProfileData(prev => ({
            ...prev,
            ...response.data
          }));
          setProfileImage(URL.createObjectURL(file));
          alert('Profile picture updated successfully!');
        }
      } catch (error) {
        console.error('Error updating profile picture:', error);
        alert('Error updating profile picture: ' + (error.response?.data?.message || error.message));
      }
    }
  };

  const handleEditImage = () => {
    fileInputRef.current?.click();
  };

  const handleSaveProfile = async () => {
    try {
      const response = await updateAdminProfile(editForm, token);
      
      if (response.success) {
        setProfileData(prev => ({
          ...prev,
          ...response.data
        }));
        setIsEditing(false);
        alert('Profile updated successfully!');
      }
    } catch (error) {
      console.error('Error updating profile:', error);
      alert('Error updating profile: ' + (error.response?.data?.message || error.message));
    }
  };

  const handleSaveSettings = async () => {
    try {
      const response = await updateAdminSettings(settingsForm, token);
      
      if (response.success) {
        setProfileData(prev => ({
          ...prev,
          settings: response.data.settings
        }));
        setIsEditingSettings(false);
        alert('Settings updated successfully!');
      }
    } catch (error) {
      console.error('Error updating settings:', error);
      alert('Error updating settings: ' + (error.response?.data?.message || error.message));
    }
  };

  const handleChangePassword = async () => {
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      alert('New passwords do not match');
      return;
    }
    
    try {
      const response = await changeAdminPassword(passwordForm, token);
      
      if (response.success) {
        setPasswordForm({
          currentPassword: '',
          newPassword: '',
          confirmPassword: ''
        });
        setIsChangingPassword(false);
        alert('Password changed successfully!');
      }
    } catch (error) {
      console.error('Error changing password:', error);
      alert('Error changing password: ' + (error.response?.data?.message || error.message));
    }
  };

  const handleCancelEdit = () => {
    setEditForm({
      name: profileData.name,
      phone: profileData.phone,
      department: profileData.department
    });
    setIsEditing(false);
  };

  const handleCancelPasswordChange = () => {
    setPasswordForm({
      currentPassword: '',
      newPassword: '',
      confirmPassword: ''
    });
    setIsChangingPassword(false);
  };

  const handleCancelSettingsEdit = () => {
    setSettingsForm(profileData.settings || {});
    setIsEditingSettings(false);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatTime = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="profile-management-page">
        <div className="profile-page-header">
          <div className="skeleton-header-title"></div>
          <div className="skeleton-header-subtitle"></div>
        </div>

        <div className="profile-content">
          <div className="profile-card">
            <div className="profile-header">
              <div className="profile-image-section">
                <SkeletonProfileImage />
                <div className="profile-info">
                  <div className="skeleton-name"></div>
                  <div className="skeleton-role"></div>
                  <div className="skeleton-email"></div>
                  <div className="skeleton-department"></div>
                </div>
              </div>
              <div className="profile-stats">
                <SkeletonStatItem />
                <SkeletonStatItem />
                <SkeletonStatItem />
              </div>
            </div>
            <div className="profile-actions">
              <div className="skeleton-button"></div>
            </div>
          </div>

          <div className="profile-details-grid">
            <div className="detail-section">
              <div className="skeleton-section-title"></div>
              <div className="detail-grid">
                {[1, 2, 3, 4, 5, 6].map((i) => (
                  <SkeletonDetailItem key={i} />
                ))}
              </div>
            </div>

            <div className="detail-section">
              <div className="section-header">
                <div className="skeleton-section-title"></div>
                <div className="skeleton-button small"></div>
              </div>
              <div className="settings-grid">
                {[1, 2, 3].map((i) => (
                  <SkeletonSettingItem key={i} />
                ))}
              </div>
            </div>

            <div className="detail-section">
              <div className="skeleton-section-title"></div>
              <div className="preferences-grid">
                {[1, 2, 3].map((i) => (
                  <SkeletonPreferenceItem key={i} />
                ))}
              </div>
            </div>

            <div className="detail-section">
              <div className="skeleton-section-title"></div>
              <div className="dashboard-settings-grid">
                {[1, 2].map((i) => (
                  <SkeletonSettingItem key={i} />
                ))}
                <SkeletonPreferenceItem />
              </div>
            </div>

            <div className="detail-section">
              <div className="skeleton-section-title"></div>
              <div className="security-actions">
                <div className="skeleton-button"></div>
              </div>
            </div>

            <div className="detail-section">
              <div className="skeleton-section-title"></div>
              <div className="activity-list">
                {[1, 2, 3].map((i) => (
                  <SkeletonActivityItem key={i} />
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!profileData) {
    return (
      <div className="profile-management-page">
        <div className="error">Error loading profile data</div>
      </div>
    );
  }

  return (
    <div className="profile-management-page">
      <div className="profile-page-header">
        <h1>Profile Management</h1>
        <p>Manage your account settings and preferences</p>
      </div>

      <div className="profile-content">
        <div className="profile-card">
          <div className="profile-header">
            <div className="profile-image-section">
              <div className="profile-image-container">
                <img 
                  src={profileImage || `http://192.168.29.29:5000${profileData.profilePicture}`}
                  alt="Profile" 
                  className="profile-image"
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = 'https://via.placeholder.com/120x120?text=Profile';
                  }}
                />
                <button 
                  className="edit-image-btn"
                  onClick={handleEditImage}
                >
                  <span>✎</span>
                </button>
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleImageUpload}
                  accept="image/*"
                  style={{ display: 'none' }}
                />
              </div>
              <div className="profile-info">
                <h2>{profileData.name}</h2>
                <p className="profile-role">{profileData.role}</p>
                <p className="profile-email">{profileData.email}</p>
                <p className="profile-department">{profileData.department}</p>
              </div>
            </div>
            <div className="profile-stats">
              <div className="stat-item">
                <span className="stat-number">{profileData.stats?.totalLogins || 0}</span>
                <span className="stat-label">Total Logins</span>
              </div>
              <div className="stat-item">
                <span className="stat-number">{profileData.stats?.loginStreak || 0}</span>
                <span className="stat-label">Login Streak</span>
              </div>
              <div className="stat-item">
                <span className="stat-number">
                  {profileData.lastLogin ? formatDate(profileData.lastLogin) : 'Never'}
                </span>
                <span className="stat-label">Last Login</span>
              </div>
            </div>
          </div>

          <div className="profile-actions">
            {!isEditing ? (
              <button 
                className="edit-profile-btn"
                onClick={() => setIsEditing(true)}
              >
                Edit Profile
              </button>
            ) : (
              <div className="edit-actions">
                <button 
                  className="save-btn"
                  onClick={handleSaveProfile}
                >
                  Save Changes
                </button>
                <button 
                  className="cancel-btn"
                  onClick={handleCancelEdit}
                >
                  Cancel
                </button>
              </div>
            )}
          </div>
        </div>


        <div className="profile-details-grid">

          <div className="detail-section">
            <h3>Personal Information</h3>
            <div className="detail-grid">
              <div className="detail-item">
                <label>Full Name</label>
                {isEditing ? (
                  <input
                    type="text"
                    name="name"
                    value={editForm.name || ''}
                    onChange={handleInputChange}
                    className="edit-input"
                  />
                ) : (
                  <span>{profileData.name}</span>
                )}
              </div>
              
              <div className="detail-item">
                <label>Email Address</label>
                <span>{profileData.email}</span>
              </div>
              
              <div className="detail-item">
                <label>Phone Number</label>
                {isEditing ? (
                  <input
                    type="tel"
                    name="phone"
                    value={editForm.phone || ''}
                    onChange={handleInputChange}
                    className="edit-input"
                    placeholder="Enter phone number"
                  />
                ) : (
                  <span>{profileData.phone || 'Not set'}</span>
                )}
              </div>
              
              <div className="detail-item">
                <label>Department</label>
                {isEditing ? (
                  <input
                    type="text"
                    name="department"
                    value={editForm.department || ''}
                    onChange={handleInputChange}
                    className="edit-input"
                  />
                ) : (
                  <span>{profileData.department}</span>
                )}
              </div>
              
              <div className="detail-item">
                <label>Join Date</label>
                <span>{formatDate(profileData.createdAt)}</span>
              </div>
              
              <div className="detail-item">
                <label>Last Updated</label>
                <span>{formatDate(profileData.updatedAt)}</span>
              </div>
            </div>
          </div>


          <div className="detail-section">
            <div className="section-header">
              <h3>Account Settings</h3>
              {!isEditingSettings ? (
                <button 
                  className="edit-settings-btn"
                  onClick={() => setIsEditingSettings(true)}
                >
                  Edit Settings
                </button>
              ) : (
                <div className="edit-actions">
                  <button 
                    className="save-btn small"
                    onClick={handleSaveSettings}
                  >
                    Save
                  </button>
                  <button 
                    className="cancel-btn small"
                    onClick={handleCancelSettingsEdit}
                  >
                    Cancel
                  </button>
                </div>
              )}
            </div>
            
            <div className="settings-grid">
              <div className="setting-item">
                <label>Language</label>
                {isEditingSettings ? (
                  <select
                    name="language"
                    value={settingsForm.language || 'en'}
                    onChange={handleSettingsChange}
                    className="edit-select"
                  >
                    <option value="en">English</option>
                    <option value="hi">Hindi</option>
                    <option value="es">Spanish</option>
                    <option value="fr">French</option>
                    <option value="de">German</option>
                  </select>
                ) : (
                  <span>
                    {settingsForm.language === 'en' ? 'English' :
                     settingsForm.language === 'hi' ? 'Hindi' :
                     settingsForm.language === 'es' ? 'Spanish' :
                     settingsForm.language === 'fr' ? 'French' :
                     settingsForm.language === 'de' ? 'German' : 'English'}
                  </span>
                )}
              </div>

              <div className="setting-item">
                <label>Timezone</label>
                {isEditingSettings ? (
                  <select
                    name="timezone"
                    value={settingsForm.timezone || 'UTC'}
                    onChange={handleSettingsChange}
                    className="edit-select"
                  >
                    <option value="UTC">UTC</option>
                    <option value="Asia/Kolkata">India (IST)</option>
                    <option value="America/New_York">New York (EST)</option>
                    <option value="Europe/London">London (GMT)</option>
                    <option value="Asia/Tokyo">Tokyo (JST)</option>
                  </select>
                ) : (
                  <span>{settingsForm.timezone || 'UTC'}</span>
                )}
              </div>

              <div className="setting-item">
                <label>Theme</label>
                {isEditingSettings ? (
                  <select
                    name="theme"
                    value={settingsForm.theme || 'light'}
                    onChange={handleSettingsChange}
                    className="edit-select"
                  >
                    <option value="light">Light</option>
                    <option value="dark">Dark</option>
                    <option value="system">System</option>
                  </select>
                ) : (
                  <span>
                    {settingsForm.theme === 'light' ? 'Light' :
                     settingsForm.theme === 'dark' ? 'Dark' :
                     settingsForm.theme === 'system' ? 'System' : 'Light'}
                  </span>
                )}
              </div>
            </div>
          </div>


          <div className="detail-section">
            <h3>Notification Preferences</h3>
            <div className="preferences-grid">
              <div className="preference-item">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    name="notifications"
                    checked={settingsForm.notifications !== false}
                    onChange={handleSettingsChange}
                    disabled={!isEditingSettings}
                  />
                  <span className="checkmark"></span>
                  Push Notifications
                </label>
              </div>
              
              <div className="preference-item">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    name="emailNotifications"
                    checked={settingsForm.emailNotifications !== false}
                    onChange={handleSettingsChange}
                    disabled={!isEditingSettings}
                  />
                  <span className="checkmark"></span>
                  Email Notifications
                </label>
              </div>
              
              <div className="preference-item">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    name="smsNotifications"
                    checked={settingsForm.smsNotifications || false}
                    onChange={handleSettingsChange}
                    disabled={!isEditingSettings}
                  />
                  <span className="checkmark"></span>
                  SMS Notifications
                </label>
              </div>
            </div>
          </div>


          <div className="detail-section">
            <h3>Dashboard Preferences</h3>
            <div className="dashboard-settings-grid">
              <div className="setting-item">
                <label>Default View</label>
                {isEditingSettings ? (
                  <select
                    name="defaultView"
                    value={settingsForm.dashboardPreferences?.defaultView || 'overview'}
                    onChange={handleDashboardSettingsChange}
                    className="edit-select"
                  >
                    <option value="overview">Overview</option>
                    <option value="analytics">Analytics</option>
                    <option value="reports">Reports</option>
                    <option value="management">Management</option>
                  </select>
                ) : (
                  <span>
                    {settingsForm.dashboardPreferences?.defaultView === 'overview' ? 'Overview' :
                     settingsForm.dashboardPreferences?.defaultView === 'analytics' ? 'Analytics' :
                     settingsForm.dashboardPreferences?.defaultView === 'reports' ? 'Reports' :
                     settingsForm.dashboardPreferences?.defaultView === 'management' ? 'Management' : 'Overview'}
                  </span>
                )}
              </div>

              <div className="setting-item">
                <label>Refresh Interval</label>
                {isEditingSettings ? (
                  <select
                    name="refreshInterval"
                    value={settingsForm.dashboardPreferences?.refreshInterval || 300000}
                    onChange={handleDashboardSettingsChange}
                    className="edit-select"
                  >
                    <option value={60000}>1 minute</option>
                    <option value={300000}>5 minutes</option>
                    <option value={600000}>10 minutes</option>
                    <option value={900000}>15 minutes</option>
                    <option value={1800000}>30 minutes</option>
                  </select>
                ) : (
                  <span>
                    {(settingsForm.dashboardPreferences?.refreshInterval || 300000) / 60000} minutes
                  </span>
                )}
              </div>

              <div className="preference-item full-width">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    name="showCharts"
                    checked={settingsForm.dashboardPreferences?.showCharts !== false}
                    onChange={handleDashboardSettingsChange}
                    disabled={!isEditingSettings}
                  />
                  <span className="checkmark"></span>
                  Show Charts on Dashboard
                </label>
              </div>
            </div>
          </div>


          <div className="detail-section">
            <h3>Security</h3>
            <div className="security-actions">
              <button 
                className="security-btn change-password"
                onClick={() => setIsChangingPassword(true)}
              >
                Change Password
              </button>
            </div>
          </div>


          {profileData.recentActivity && profileData.recentActivity.length > 0 && (
            <div className="detail-section">
              <h3>Recent Activity</h3>
              <div className="activity-list">
                {profileData.recentActivity.map((activity, index) => (
                  <div key={index} className="activity-item">
                    <div className="activity-info">
                      <span className="activity-type">Login</span>
                      <span className="activity-ip">IP: {activity.ip}</span>
                    </div>
                    <span className="activity-time">
                      {formatDate(activity.timestamp)} at {formatTime(activity.timestamp)}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>


      {isChangingPassword && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>Change Password</h3>
              <button 
                className="modal-close"
                onClick={handleCancelPasswordChange}
              >
                ×
              </button>
            </div>
            <div className="modal-body">
              <div className="password-form">
                <div className="form-group">
                  <label>Current Password</label>
                  <input
                    type="password"
                    name="currentPassword"
                    value={passwordForm.currentPassword}
                    onChange={handlePasswordChange}
                    className="form-input"
                    placeholder="Enter current password"
                  />
                </div>
                <div className="form-group">
                  <label>New Password</label>
                  <input
                    type="password"
                    name="newPassword"
                    value={passwordForm.newPassword}
                    onChange={handlePasswordChange}
                    className="form-input"
                    placeholder="Enter new password"
                  />
                </div>
                <div className="form-group">
                  <label>Confirm New Password</label>
                  <input
                    type="password"
                    name="confirmPassword"
                    value={passwordForm.confirmPassword}
                    onChange={handlePasswordChange}
                    className="form-input"
                    placeholder="Confirm new password"
                  />
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button 
                className="cancel-btn"
                onClick={handleCancelPasswordChange}
              >
                Cancel
              </button>
              <button 
                className="save-btn"
                onClick={handleChangePassword}
              >
                Change Password
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProfileManagement;